"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ChannelCreator = void 0;
const react_1 = __importDefault(require("react"));
const stream_chat_react_1 = require("stream-chat-react");
const i18n_1 = require("@app/util/i18n");
const ui_1 = require("@app/component/ui");
const store_1 = require("@app/store");
const useTeamMembers_1 = require("@app/util/hooks/chat/useTeamMembers");
const ac_1 = require("@app/ac");
const error_handler_1 = require("@app/util/error-handler");
const maybe_unwrap_1 = require("@app/util/apollo/maybe-unwrap");
function ChannelCreator() {
    const dispatch = (0, store_1.useRootDispatch)();
    const workspaceId = (0, store_1.useRootSelector)((state) => state.workspaceId);
    const { client, setActiveChannel } = (0, stream_chat_react_1.useChatContext)();
    const { teamMembers, isFetching } = (0, useTeamMembers_1.useTeamMembers)({
        onError: () => {
            void dispatch((0, ac_1.setModal)(null));
        },
        workspaceId,
    });
    const [isSaving, setIsSaving] = react_1.default.useState(false);
    const [selectedMembers, setSelectedMembers] = react_1.default.useState([]);
    const [groupName, setGroupName] = react_1.default.useState('');
    const [step, setStep] = react_1.default.useState('selection');
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    function findExistingChannel() {
        // only 1v1 "messaging" type chats are uniqued
        if (selectedMembers.length > 1)
            return undefined;
        // eslint-disable-next-line @typescript-eslint/require-array-sort-compare
        const members = [client.userID, ...selectedMembers].sort();
        const currentChannels = Object.values(client.activeChannels).filter((ch) => ch.data?.type === 'messaging' && ch.data?.team === workspaceId);
        const channelWithSameMembers = currentChannels.find((ch) => {
            // eslint-disable-next-line @typescript-eslint/require-array-sort-compare
            const channelMembers = Object.values(ch.state.members)
                .map((m) => m.user_id)
                .sort();
            return channelMembers.toString() === members.toString();
        });
        return channelWithSameMembers;
    }
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    async function createChannel() {
        try {
            if (!client.userID)
                return;
            setIsSaving(true);
            if (selectedMembers.length === 1) {
                const existingChannels = await client.queryChannels({
                    members: {
                        $in: [selectedMembers[0]],
                    },
                    type: 'messaging',
                    joined: true,
                    team: workspaceId,
                });
                const existingChannel = existingChannels[0];
                if (existingChannel) {
                    setActiveChannel(existingChannel);
                    return;
                }
            }
            const members = [client.userID, ...selectedMembers];
            const channelId = window.crypto.randomUUID();
            const isGroupChat = members.length > 2;
            if (isGroupChat && !groupName)
                return;
            const channel = client.channel(isGroupChat ? 'group' : 'messaging', channelId, {
                members,
                team: workspaceId,
                name: isGroupChat ? groupName : undefined,
            });
            await new Promise((resolve) => setTimeout(resolve, 3000));
            await channel.create();
            setActiveChannel(channel);
        }
        catch (error) {
            console.error('createChannel failed', { error });
            (0, error_handler_1.processErrors)([], (0, maybe_unwrap_1.unwrapMaybe)((0, i18n_1.t)('ERR_MESSAGE.error')));
        }
        finally {
            void dispatch((0, ac_1.setModal)(null));
        }
    }
    return (react_1.default.createElement(ui_1.Modal, { size: ui_1.Modal.SIZES.M, headerContent: react_1.default.createElement("div", { className: 'ds-title' }, (0, i18n_1.t)('NEW_CHAT')), footerContent: isFetching ? undefined : (react_1.default.createElement(ui_1.Flex, { grow: 1 },
            react_1.default.createElement(ui_1.Button, { size: ui_1.Button.SIZES.FULL, label: (0, i18n_1.t)('CANCEL'), disabled: isSaving, onClick: () => {
                    if (step === 'naming') {
                        setStep('selection');
                    }
                    else {
                        void dispatch((0, ac_1.setModal)(null));
                    }
                } }),
            react_1.default.createElement(ui_1.Button, { size: ui_1.Button.SIZES.FULL, style: ui_1.Button.STYLES.CONTAINED, label: (0, i18n_1.t)(step === 'naming' || selectedMembers.length <= 1
                    ? 'CREATE'
                    : 'MA_FORM_CONTINUE'), loading: isSaving, disabled: selectedMembers.length === 0 || (step === 'naming' && !groupName), onClick: async () => {
                    const existingChannel = findExistingChannel();
                    if (existingChannel) {
                        setActiveChannel(existingChannel);
                        void dispatch((0, ac_1.setModal)(null));
                        return;
                    }
                    if (selectedMembers.length > 1 && step !== 'naming') {
                        setStep('naming');
                    }
                    else {
                        void createChannel();
                    }
                } }))), sections: [
            react_1.default.createElement("div", { key: 'first-section' }, isFetching || isSaving ? (react_1.default.createElement(ui_1.Loader, { size: ui_1.Loader.SIZES.SMALL })) : step === 'selection' ? (react_1.default.createElement(ui_1.EmployeeList, { key: 'members', withSearch: true, employees: teamMembers, selected: selectedMembers, onSelectChange: (employee) => {
                    setSelectedMembers((current) => {
                        if (current.includes(employee.id)) {
                            return current.filter((memberId) => memberId !== employee.id);
                        }
                        return [...current, employee.id];
                    });
                } })) : (react_1.default.createElement("div", null,
                react_1.default.createElement(ui_1.Input, { value: groupName, onChange: setGroupName, required: true, placeholder: (0, maybe_unwrap_1.unwrapMaybe)((0, i18n_1.t)('NAME_V2')), label: (0, i18n_1.t)('ORG_SP_TRANSFERS_GROUPS_ADD_NAME_LABEL'), size: ui_1.Input.SIZES.FULL_WIDTH })))),
        ] }));
}
exports.ChannelCreator = ChannelCreator;
